import { FC } from 'react';
import { useRecoilValue } from 'recoil';
import BootstrapCol from 'react-bootstrap/Col';
import BootstrapForm from 'react-bootstrap/Form';
import BootstrapRow from 'react-bootstrap/Row';
import { formatTimestamp } from 'common/util/time';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Dialog from 'components/common/Dialog/Dialog';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { ExportJobFormData, ExportJobTableData, JobStatus } from 'components/bulkExports/types';
import ExportStatus from './ExportStatus';

import './BulkExportsModal.scss';

/**
 * Bulk exports modal component which shows data of specific job.
 * @param {Object}     currentItem     Job data to show.
 * @param {boolean}    showModal       Boolean to show modal or not.
 * @param {Function}   onClone         To handle 'clone' button function.
 * @param {Function}   onCancel        To handle 'cancel' button function.
 * @param {Function}   onClose         To handle modal closing.
 */

interface BulkExportsModalProps {
  currentItem: ExportJobTableData | null;
  showModal: boolean;
  onClone: (exportData: ExportJobFormData) => void;
  onCancel: (cancelJobId: string) => void;
  onClose: () => void;
}

const BulkExportsModal: FC<BulkExportsModalProps> = ({
  showModal, currentItem, onClone, onCancel, onClose,
}: BulkExportsModalProps) => {
  const timezone = useRecoilValue(timezoneState);
  
  if (!currentItem) {
    return null;
  }

  const handleClone = (): void => {
    const item: ExportJobFormData = {
      query: currentItem.query,
      label: currentItem.label,
      s3Bucket: currentItem.s3Bucket,
      filenamePrefix: currentItem.filenamePrefix,
      timeRangeStart: currentItem.timeRangeStart,
      timeRangeEnd: currentItem.timeRangeEnd,
      recipients: currentItem.recipients,
    };
    onClone(item);
    onClose();
  };

  const handleCancel = (): void => {
    onCancel(currentItem.id);
    onClose();
  };

  const footerProps = {
    leftItems: [
      <>
        {(currentItem.status === JobStatus.PENDING
        || currentItem.status === JobStatus.WORKING)
          ? <Button onClick={handleCancel}>Cancel Job</Button> : []}
      </>,
    ],
    rightItems: [
      <Button variant={ButtonVariant.PRIMARY} onClick={handleClone}>
        Clone
      </Button>,
    ],
    className: 'LogViewSettingsDialog__Footer',
  };

  const dialogContent = (
    <div className="BulkExportsModal__ModalBody">
      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Requested By</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={currentItem.createdBy} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Requested On</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control 
            type="text" 
            readOnly 
            plaintext 
            defaultValue={formatTimestamp(currentItem.createdTimestamp, timezone)} 
          />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Time Range</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={`${currentItem.timeRangeStart} - ${currentItem.timeRangeEnd}`} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Query</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={currentItem.query} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Label</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={currentItem.label} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">S3 Bucket</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={currentItem.s3Bucket} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Filename Prefix</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={currentItem.filenamePrefix} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Output</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={currentItem.output} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Email Notification</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <BootstrapForm.Control type="text" readOnly plaintext defaultValue={currentItem.recipients.join(', ')} />
        </BootstrapCol>
      </BootstrapForm.Group>

      <BootstrapForm.Group as={BootstrapRow}>
        <BootstrapForm.Label column sm="4">Status</BootstrapForm.Label>
        <BootstrapCol sm="8">
          <ExportStatus status={currentItem.status} completionPercent={currentItem.completionPercent} />
        </BootstrapCol>
      </BootstrapForm.Group>
    </div>
  );

  return (
    <Dialog show={showModal} close={onClose} title="Export Details" footer={footerProps}>
      {dialogContent}
    </Dialog>
  );
};

export default BulkExportsModal;
